import { memo, useState } from 'react';

import { styled } from '@mui/material';

import { useParams } from 'react-router';

import {
  useGetScenarioPartialsQuery,
  useGetSharedScenarioPage,
} from '@app-velocity/modules/scenarios';

import { TAiSimulationPartialDto } from '@app-velocity/av-api-v2';

import { Skeleton } from '@app-velocity/ui';

import { ViewPreviewContent } from '@app-velocity/libs/report';
import { isPageData } from '@app-velocity/common';

export type ViewPreviewProps = {
  pageId: string;
  partials: TAiSimulationPartialDto[];
  clicks?: { x: number; y: number }[];
  size: 'full' | 'large' | 'normal';
  persona: 'accessible' | 'default' | string;
  targetHeight?: number;
  highlight?: boolean;
};

const StyledSkeleton = styled(Skeleton)(({ theme }) => ({
  maxWidth: '100%',
  minWidth: theme.spacing(50),
}));

const getSize = (size: 'full' | 'large' | 'normal') => {
  switch (size) {
    case 'full':
      return document.body.offsetHeight - 100;
    case 'large':
      return 500;
    default:
      return 250;
  }
};

export const ViewPreview = memo(
  ({ pageId, partials, clicks, size, persona, highlight }: ViewPreviewProps) => {
    const { shareToken } = useParams<{ shareToken: string }>();

    const [skeletonSeed] = useState(() => Math.random() + 0.5);

    const { data: fullPage } = useGetSharedScenarioPage(
      shareToken || '',
      pageId || '',
      'report'
    );

    const { data: partialsResults, isLoading } = useGetScenarioPartialsQuery(
      shareToken || '',
      partials.map((p) => p.id || '')
    );

    const targetHeight = getSize(size) - 8;

    if (!fullPage || !isPageData(fullPage.data))
      return (
        <StyledSkeleton
          height={targetHeight}
          width={Math.round(targetHeight * skeletonSeed)}
        />
      );

    const { width, height, density } = fullPage.data;

    if (!width || !height) return null;

    const factor = height / targetHeight;
    const targetWidth = width / factor;

    if (isLoading)
      return <StyledSkeleton height={targetHeight} width={targetWidth} />;

    return (
      <ViewPreviewContent
        clicks={clicks}
        persona={persona}
        size={size}
        partials={partials}
        page={{
          width,
          height,
          image: fullPage.image || '',
          density: density || 1,
          actions: fullPage.data.actions,
        }}
        highlight={highlight}
        partialsResults={partialsResults}
      />
    );
  }
);
